import { Injectable, NgZone } from '@angular/core';
import {
    EXTENSION_BRIDGE_VERSION,
    ExtensionAddResult,
    ExtensionContext,
    ExtensionItemSpec,
    ExtensionPingResult,
    ExtensionProvider
} from '@models/ExtensionBridge';
import { EXTENSION_SUPPORTED_ITEM_TYPES, getExtensionWidgetCatalog } from '@models/ExtensionWidgetFields';

const BRIDGE_KEY = '__vsightExt';
const APP_NAME = 'vsight-workflow';

@Injectable({
    providedIn: 'root'
})
export class ExtensionBridgeService {

    private provider: ExtensionProvider = null;
    private installed = false;

    constructor(private zone: NgZone) {
        this.install();
    }

    install(): void {
        if (this.installed || typeof window === 'undefined') {
            return;
        }
        this.installed = true;
        (window as any)[BRIDGE_KEY] = {
            version: EXTENSION_BRIDGE_VERSION,
            ping: () => this.ping(),
            getContext: () => this.getContext(),
            listItems: () => EXTENSION_SUPPORTED_ITEM_TYPES.slice(),
            getCatalog: () => this.getCatalog(),
            addItem: (spec: ExtensionItemSpec) => this.addItem(spec)
        };
    }

    registerProvider(provider: ExtensionProvider): void {
        this.provider = provider;
    }

    unregisterProvider(provider: ExtensionProvider): void {
        if (this.provider === provider) {
            this.provider = null;
        }
    }

    ping(): ExtensionPingResult {
        return {
            ok: true,
            app: APP_NAME,
            version: EXTENSION_BRIDGE_VERSION,
            ready: !!this.provider,
            reason: this.provider ? undefined : 'WORKFLOW_NOT_OPEN'
        };
    }

    getContext(): ExtensionContext {
        if (!this.provider) {
            return {
                ready: false,
                reason: 'WORKFLOW_NOT_OPEN',
                app: APP_NAME,
                version: EXTENSION_BRIDGE_VERSION,
                supportedItemTypes: EXTENSION_SUPPORTED_ITEM_TYPES.slice()
            };
        }
        return this.provider.getContext();
    }
    getCatalog() {
        return getExtensionWidgetCatalog();
    }

    addItem(spec: ExtensionItemSpec): Promise<ExtensionAddResult> {
        return new Promise<ExtensionAddResult>(resolve => {
            if (!this.provider) {
                resolve({ ok: false, error: 'WORKFLOW_NOT_OPEN' });
                return;
            }
            this.zone.run(() => {
                try {
                    Promise.resolve(this.provider.addItem(spec || ({} as ExtensionItemSpec))).then(
                        result => resolve(result),
                        error => resolve({ ok: false, error: this.describeError(error) })
                    );
                } catch (error) {
                    resolve({ ok: false, error: this.describeError(error) });
                }
            });
        });
    }

    private describeError(error: any): string {
        if (!error) {
            return 'UNKNOWN_ERROR';
        }
        return error.message ? error.message : String(error);
    }
}
